import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { District } from '@citizen-walk/shared-types';
import { useGeneratePmtiles } from './useGeneratePmtiles';
import { JP_REGION_OPTIONS, bboxFromCircle, regionsOverlappingBBox } from './jpRegions';

/**
 * PMTiles 生成パネル (Phase 5.3 R-33 step 3、admin DistrictDetailPage 内 section)。
 *
 * 機能:
 *   - 現状 mapPmtiles メタデータ表示 (sizeBytes / maxZoom / generatedAt / schema / region)
 *   - maxZoom 選択 (14 / 15)
 *   - 「生成実行」ボタン → generatePmtiles callable 呼出 (~数十秒〜数分、timeout 540s)
 *   - 実行中 / 成功 / 失敗の UI 状態表示
 *
 * 前提:
 *   - 地区 doc に `postAreaCenter` / `postAreaRadiusM` が設定済 (admin の Phase 5 sprint で write UI)
 *   - master role (callable 側で `requireMaster`)
 *   - 環境変数 `OMT_URL_<REGION>` または `PROTOMAPS_URL` 設定済 (Cloud Functions Config)
 *   - functions/bin/pmtiles バイナリ deploy 済 (`fetch-pmtiles-binary.sh`)
 *
 * 未設定時の失敗パターンと UI:
 *   - postAreaCenter/RadiusM 未設定 → failed-precondition
 *   - 環境変数未設定 → failed-precondition (No upstream pmtiles URL configured)
 *   - バイナリ未配置 → failed-precondition (pmtiles binary not found)
 *   - master role なし → permission-denied
 */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatIsoDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface DistrictPmtilesPanelProps {
  district: District;
}

export function DistrictPmtilesPanel({ district }: DistrictPmtilesPanelProps) {
  const [maxZoom, setMaxZoom] = useState<14 | 15 | 16>(14);
  const { state, run } = useGeneratePmtiles();
  const { toast } = useToast();
  const meta = district.mapPmtiles;
  const canRun =
    state.status !== 'pending' && !!district.postAreaCenter && !!district.postAreaRadiusM;

  // 地区 bbox に重なる地方を既定選択にする (県境をまたぐ地区は複数が pre-check され合成される)。
  const overlappingRegions = useMemo(() => {
    const c = district.postAreaCenter;
    const r = district.postAreaRadiusM;
    if (!c || !r) return [];
    return regionsOverlappingBBox(bboxFromCircle(c.latitude, c.longitude, r));
  }, [district.postAreaCenter, district.postAreaRadiusM]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(overlappingRegions);
  // 地区 (= postAreaCenter/RadiusM) が変わったら既定選択を取り直す。useState 初期値は初回マウント
  // 時のみ評価されるため、同一コンポーネントが別地区で再利用されると前地区の選択が残るのを防ぐ。
  useEffect(() => {
    setSelectedRegions(overlappingRegions);
  }, [overlappingRegions]);
  const isCrossRegion = selectedRegions.length > 1;

  function toggleRegion(code: string) {
    setSelectedRegions((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  // 成功 / 失敗の状態遷移時に 1 回 toast を出す (重複発火防止に prev status を ref で記録)
  const prevStatusRef = useRef(state.status);
  useEffect(() => {
    if (prevStatusRef.current !== state.status) {
      if (state.status === 'success') {
        toast('success', 'PMTiles 切出が完了しました');
      } else if (state.status === 'error') {
        toast('error', `PMTiles 切出に失敗: ${state.error.message}`);
      }
      prevStatusRef.current = state.status;
    }
  }, [state, toast]);

  async function onGenerate() {
    if (!canRun) return;
    // 1 件以上選択時はその地方を使用 (複数なら合成)。未選択なら regions 省略 = 中心から自動判定。
    await run({
      districtId: district.id,
      maxZoom,
      ...(selectedRegions.length > 0 ? { regions: selectedRegions } : {}),
    });
  }

  return (
    <Card padding="lg" className="border-primary-200">
      <h3 className="text-base font-bold text-slate-900 mb-3">
        🗺️ PMTiles 切出 (オフラインマップ)
      </h3>

      {/* AW10 (docs/108 Critical #15): 生成済 PMTiles の実描画検証への導線 */}
      {meta && (
        <Link
          to={`/districts/${district.id}/pmtiles-inspector`}
          className="inline-block mb-3 text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          🔍 検査ツールで実描画を確認する →
        </Link>
      )}

      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">現在の生成済データ</h4>
        {meta ? (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">Storage path</dt>
            <dd className="text-slate-800 break-all">
              <code>{meta.storagePath}</code>
            </dd>
            <dt className="text-slate-500">サイズ</dt>
            <dd className="text-slate-800">{formatBytes(meta.sizeBytes)}</dd>
            <dt className="text-slate-500">maxZoom</dt>
            <dd className="text-slate-800">{meta.maxZoom}</dd>
            <dt className="text-slate-500">生成日時</dt>
            <dd className="text-slate-800">{formatIsoDate(meta.generatedAt)}</dd>
            <dt className="text-slate-500">planetSourceVersion</dt>
            <dd className="text-slate-800">
              <code>{meta.planetSourceVersion}</code>
            </dd>
            <dt className="text-slate-500">schema</dt>
            <dd className="text-slate-800">{meta.schema ?? 'protomaps (default)'}</dd>
            {(meta.regions?.length ?? 0) > 0 ? (
              <>
                <dt className="text-slate-500">使用エリア</dt>
                <dd className="text-slate-800">
                  {meta.regions?.join(' + ')}
                  {(meta.regions?.length ?? 0) > 1 && (
                    <span className="text-primary-600"> (合成)</span>
                  )}
                </dd>
              </>
            ) : (
              meta.region && (
                <>
                  <dt className="text-slate-500">region</dt>
                  <dd className="text-slate-800">{meta.region}</dd>
                </>
              )
            )}
          </dl>
        ) : (
          <p className="text-xs text-slate-500">未生成 (本地区はまだ PMTiles を切出していません)</p>
        )}
      </section>

      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">前提条件</h4>
        <ul className="text-xs text-slate-700 space-y-1">
          <li>
            postAreaCenter:{' '}
            {district.postAreaCenter ? (
              <code>
                ({district.postAreaCenter.latitude}, {district.postAreaCenter.longitude})
              </code>
            ) : (
              <span className="text-danger-600">未設定</span>
            )}
          </li>
          <li>
            postAreaRadiusM:{' '}
            {district.postAreaRadiusM ? (
              <code>{district.postAreaRadiusM} m</code>
            ) : (
              <span className="text-danger-600">未設定</span>
            )}
          </li>
        </ul>
        {!canRun && state.status !== 'pending' && (
          <p className="mt-2 text-xs text-danger-700">
            ※ postAreaCenter / postAreaRadiusM が未設定です。地区 CRUD UI (Phase 5 admin sprint) で
            設定後に再表示してください。
          </p>
        )}
      </section>

      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">生成設定</h4>
        <label htmlFor="pmtiles-maxzoom" className="flex items-center gap-2 text-xs mb-3">
          <span className="text-slate-700">maxZoom:</span>
          <select
            id="pmtiles-maxzoom"
            aria-label="PMTiles 切出時の最大 zoom レベル"
            className="border border-slate-300 rounded px-2 py-1 text-xs"
            value={maxZoom}
            onChange={(e) => setMaxZoom(Number(e.target.value) as 14 | 15 | 16)}
            disabled={state.status === 'pending'}
          >
            <option value={14}>14 (広域、~数十 MB)</option>
            <option value={15}>15 (建物レベル、~数百 MB)</option>
            <option value={16}>16 (建物+番地レベル、高精細・大容量)</option>
          </select>
        </label>

        {/* 地方 (使用エリア) 選択。県境をまたぐ地区は複数選択で各地方の OMT を合成する。
            既定は地区範囲に重なる地方を pre-check。全解除すると中心から自動判定 (従来挙動)。 */}
        <fieldset disabled={state.status === 'pending'}>
          <legend className="text-slate-700 text-xs mb-1">
            使用するエリア (地方){' '}
            {isCrossRegion && (
              <span className="text-primary-600 font-bold">
                — {selectedRegions.length} 地方を合成
              </span>
            )}
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {JP_REGION_OPTIONS.map((r) => {
              const checked = selectedRegions.includes(r.code);
              const suggested = overlappingRegions.includes(r.code);
              return (
                <label
                  key={r.code}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer ${
                    checked
                      ? 'bg-primary-50 border-primary-400 text-primary-800'
                      : 'bg-white border-slate-300 text-slate-600'
                  }`}
                  title={suggested ? 'この地区の範囲に重なる地方 (推奨)' : undefined}
                >
                  <input
                    type="checkbox"
                    className="accent-primary-600"
                    checked={checked}
                    onChange={() => toggleRegion(r.code)}
                  />
                  {r.label}
                  {suggested && <span className="text-primary-500">●</span>}
                </label>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
            ● = 地区範囲に重なる地方 (既定で選択)。県境をまたぐ地区 (例: 箱根) は複数選択すると、
            各地方の地図から必要範囲を合成して 1
            ファイルにします。全て外すと中心座標から自動判定します。
          </p>
        </fieldset>
      </section>

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            if (canRun) toast('info', 'PMTiles 切出を開始しました (~数十秒〜数分)');
            void onGenerate();
          }}
          disabled={!canRun}
        >
          {state.status === 'pending' ? '生成中…' : '生成実行'}
        </Button>
        {state.status === 'pending' && (
          <span className="text-xs text-slate-500">
            ※ 本処理は数十秒〜数分かかります。ページを閉じずにお待ちください。
          </span>
        )}
      </div>

      {state.status === 'success' && (
        <p className="mt-3 text-xs text-success-600">
          ✅ 生成完了 ({(state.result.elapsedMs / 1000).toFixed(1)}s、
          {formatBytes(state.result.sizeBytes)})
        </p>
      )}
      {state.status === 'error' && (
        <p className="mt-3 text-xs text-danger-700 break-words">
          ❌ 失敗: <code>{state.error.message}</code>
        </p>
      )}
    </Card>
  );
}
